import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { ArrowsLeftRight, MagicWand, Check, Link, DownloadSimple, FileXls } from '@phosphor-icons/react';

export const MatchingToolbar = ({
  clients, selectedClient, setSelectedClient,
  autoMatching, onAutoMatch, matchedCount,
  exporting, onExportMatched, onExportAll,
}) => (
  <>
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="heading-2 mb-1">Transaction Matching</h1>
        <p className="text-muted-foreground">Match bank transactions to invoices</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedClient} onValueChange={setSelectedClient}>
          <SelectTrigger className="w-48" data-testid="matching-client-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Button onClick={onAutoMatch} disabled={autoMatching || selectedClient === 'all'} data-testid="auto-match-btn">
          {autoMatching ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <MagicWand className="w-4 h-4 mr-2" />}
          Auto Match
        </Button>
      </div>
    </div>
    <Card className="border border-border">
      <CardContent className="py-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{matchedCount}</span> matched transactions ready for export
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onExportMatched} disabled={exporting || matchedCount === 0} data-testid="export-matched-btn">
              <FileXls className="w-4 h-4 mr-2" /> Export Matched (CSV)
            </Button>
            <Button variant="outline" onClick={onExportAll} disabled={exporting} data-testid="export-all-btn">
              <DownloadSimple className="w-4 h-4 mr-2" /> Export All Transactions
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  </>
);

export const UnmatchedPanel = ({
  unmatchedTransactions, pendingInvoices,
  selectedTransaction, setSelectedTransaction,
  matchingInvoice, setMatchingInvoice,
  formatCurrency,
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <Card className="border border-border">
      <CardHeader className="pb-4">
        <CardTitle className="heading-4">Unmatched Transactions</CardTitle>
        <CardDescription>{unmatchedTransactions.length} transactions waiting to be matched</CardDescription>
      </CardHeader>
      <CardContent className="p-0 max-h-96 overflow-auto">
        {unmatchedTransactions.length === 0 ? (
          <div className="text-center py-8 px-4"><Check className="w-8 h-8 mx-auto mb-2 text-green-500" /><p className="text-muted-foreground">All transactions matched!</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow className="table-header"><TableHead className="w-8"></TableHead><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
            <TableBody>
              {unmatchedTransactions.map((txn) => (
                <TableRow key={txn.id} className={`cursor-pointer ${selectedTransaction === txn.id ? 'bg-primary/10' : ''}`} onClick={() => setSelectedTransaction(txn.id)} data-testid={`select-txn-${txn.id}`}>
                  <TableCell><input type="radio" checked={selectedTransaction === txn.id} onChange={() => setSelectedTransaction(txn.id)} className="w-4 h-4" /></TableCell>
                  <TableCell className="text-sm">{txn.transaction_date || '-'}</TableCell>
                  <TableCell className="text-sm max-w-32 truncate">{txn.description || '-'}</TableCell>
                  <TableCell className={`text-sm ${txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(txn.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
    <Card className="border border-border">
      <CardHeader className="pb-4">
        <CardTitle className="heading-4">Pending Invoices</CardTitle>
        <CardDescription>{pendingInvoices.length} invoices waiting to be matched</CardDescription>
      </CardHeader>
      <CardContent className="p-0 max-h-96 overflow-auto">
        {pendingInvoices.length === 0 ? (
          <div className="text-center py-8 px-4"><Check className="w-8 h-8 mx-auto mb-2 text-green-500" /><p className="text-muted-foreground">All invoices matched!</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow className="table-header"><TableHead className="w-8"></TableHead><TableHead>Type</TableHead><TableHead>Supplier</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
            <TableBody>
              {pendingInvoices.map((inv) => (
                <TableRow key={inv.id} className={`cursor-pointer ${matchingInvoice === inv.id ? 'bg-primary/10' : ''}`} onClick={() => setMatchingInvoice(inv.id)} data-testid={`select-inv-${inv.id}`}>
                  <TableCell><input type="radio" checked={matchingInvoice === inv.id} onChange={() => setMatchingInvoice(inv.id)} className="w-4 h-4" /></TableCell>
                  <TableCell><Badge variant="outline" className="capitalize text-xs">{inv.invoice_type}</Badge></TableCell>
                  <TableCell className="text-sm max-w-32 truncate">{inv.supplier_name || '-'}</TableCell>
                  <TableCell className="text-sm">{formatCurrency(inv.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  </div>
);

export const MatchedTable = ({ matchedTransactions, invoices, getClientName, formatCurrency }) => (
  <Card className="border border-border">
    <CardHeader className="pb-4">
      <CardTitle className="heading-4 flex items-center gap-2"><ArrowsLeftRight className="w-5 h-5" /> Recently Matched</CardTitle>
      <CardDescription>{matchedTransactions.length} matched transactions</CardDescription>
    </CardHeader>
    <CardContent className="p-0">
      {matchedTransactions.length === 0 ? (
        <div className="text-center py-8"><p className="text-muted-foreground">No matched transactions yet</p></div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead>Client</TableHead><TableHead>Transaction Date</TableHead><TableHead>Description</TableHead><TableHead>Amount</TableHead><TableHead>Matched Invoice</TableHead><TableHead>Confidence</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {matchedTransactions.slice(0, 10).map((txn) => {
              const matchedInvoice = invoices.find((inv) => inv.id === txn.matched_invoice_id);
              return (
                <TableRow key={txn.id} data-testid={`matched-row-${txn.id}`}>
                  <TableCell className="font-medium">{getClientName(txn.client_id)}</TableCell>
                  <TableCell>{txn.transaction_date || '-'}</TableCell>
                  <TableCell className="max-w-xs truncate">{txn.description || '-'}</TableCell>
                  <TableCell>{formatCurrency(txn.amount)}</TableCell>
                  <TableCell>{matchedInvoice ? <span className="text-sm">{matchedInvoice.supplier_name || matchedInvoice.invoice_number || 'Invoice'}</span> : '-'}</TableCell>
                  <TableCell>
                    {txn.match_confidence
                      ? <Badge variant="secondary" className="status-matched">{Math.round(txn.match_confidence * 100)}%</Badge>
                      : <Badge variant="secondary">Manual</Badge>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);
